import { AppModule } from 'app/module';
import { WatchlistButton } from 'module/watchlistButton/main';
import { WatchlistLoginInfo } from 'module/watchlistLoginInfo/main';
import { ApplyButton } from 'module/applyButton/main';
import { track as trackServerSide } from 'util/statistics';
import { ShareModal } from 'module/shareModal/main';
import { postJson } from 'util/xhr';
import { API_ENDPOINT_LOG_APPLICATION_CLICK } from 'app/endpoints';
import { log } from '@karriereat/action-logger/dist/log';
import './styles.scss';
import Template from './template.hbs';

export class JobHeader extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    ready() {
        trackServerSide({ name: 'job_view' });
    }

    domBindings() {
        this.dom.applyButtonContainer = this.dom.el.querySelector('.m-jobHeader__apply');
        this.dom.applyButton = this.dom.el.querySelector('.m-applyButton');
        this.dom.email = this.dom.el.querySelector('.m-jobHeader__email');
        this.dom.watchlistButton = this.dom.el.querySelector('.m-jobHeader__watchlist .m-watchlistButton');
        // eslint-disable-next-line max-len
        this.dom.watchlistLoginInfo = this.dom.el.querySelector('.m-jobHeader__watchlistLoginInfo .m-watchlistLoginInfo');
        this.dom.inner = this.dom.el.querySelector('.m-jobHeader__inner');
        this.dom.jobTitle = this.dom.el.querySelector('.m-jobHeader__jobTitle');
        this.dom.actions = this.dom.el.querySelector('.m-jobHeader__actions');
        this.dom.shareModal = this.dom.el.querySelector('.m-jobHeader__share .m-shareModal');
        this.dom.printButton = this.dom.el.querySelector('.m-jobHeader__print');

        // ApplyButton and ShareModal instance are needed in `getPropsFromDom()` which runs before `subs()`.
        if (this.dom.applyButton) {
            this.applyButton = new ApplyButton(this.dom.applyButton);
        }
        if (this.dom.shareModal) {
            this.shareModal = new ShareModal(this.dom.shareModal);
        }
    }

    getPropsFromDom() {
        let applyButton;
        let shareModal = null;

        if (this.dom.shareModal) {
            shareModal = this.shareModal.getPropsFromDom();
        }

        if (!this.dom.applyButton) {
            applyButton = {
                error: false,
            };
        } else {
            applyButton = this.applyButton.getPropsFromDom();
            applyButton.clickTrackingEvent = this.dom.applyButtonContainer.getAttribute('data-ga-event');
        }

        return {
            applyTextAction: this.dom.actions
                ? this.dom.actions.classList.contains('m-jobHeader__actions--hasTextAction') : false,
            jobDetail: {
                id: this.dom.el.getAttribute('data-id'),
                jobBundleId: this.dom.el.getAttribute('data-bundle-id'),
            },
            shareModal,
            applyAction: {
                applyButton,
            },
            isSplitView: this.dom.el.getAttribute('data-is-split-view'),
            jobHeader: {
                job: {
                    id: this.dom.el.getAttribute('data-id'),
                    title: this.dom.jobTitle ? this.dom.jobTitle.innerText : '',
                    isInactive: this.dom.el.getAttribute('data-is-inactive') === 'true',
                },
                company: {
                    name: this.dom.el.getAttribute('data-company-name') ?? false,
                    logoUrl: this.dom.el.getAttribute('data-logo-url') ?? false,
                },
                hideWatchlistLoginInfo: !this.dom.watchlistLoginInfo,
                isPreview: this.dom.el.getAttribute('data-is-preview') === 'true',
            },
        };
    }

    domEvents() {
        if (!this.dom.el.getAttribute('data-is-inactive') || this.dom.el.getAttribute('data-is-preview')) {
            if (this.dom.printButton) {
                this.dom.printButton.addEventListener('click', () => this.print());
            }
        }

        if (!this.dom.el.getAttribute('data-is-inactive')) {
            if (this.dom.email) {
                this.dom.email.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.shareModal.share();
                });
            }
        }
    }

    subEvents() {
        if (this.dom.watchlistLoginInfo) {
            this.watchlistLoginInfo.events.on('onCloseButtonClicked', () => this.closeWatchlistLoginInfo());
        }
        if (this.dom.shareModal) {
            this.shareModal.events.on('pushState', () => this.events.emit('pushState'));
            this.shareModal.events.on('addShareModalStyles', () => {
                this.dom.el.classList.add('m-jobHeader--openModal');
                this.events.emit('addShareModalStyles');
            });
            this.shareModal.events.on('removeShareModalStyles', () => {
                this.dom.el.classList.remove('m-jobHeader--openModal');
                this.events.emit('removeShareModalStyles');
            });
        }
        if (this.dom.applyButton) {
            this.applyButton.events.on('submitClicked', () => {
                let type = '';
                let applicationType = '';

                if (this.props.applyAction.applyButton.isSmartApplyJob) {
                    type = 'smartbewerben';
                    applicationType = 'SmartBewerben';
                } else {
                    type = 'extern';
                    applicationType = 'External';
                }

                const data = {
                    jobId: this.props.jobHeader.job.id,
                    jobBundleId: this.props.jobDetail.jobBundleId,
                    type,
                };

                trackServerSide({
                    name: 'job_apply_click',
                    params: {
                        application_type: applicationType,
                    },
                });
                log('apply-click', data);

                this.logApplicationClick(data);
                this.applyButton.redirectToApplyForm();
            });
        }
    }

    subs() {
        if (this.dom.watchlistButton) {
            this.watchlistButton = new WatchlistButton(this.dom.watchlistButton);
        }
        if (this.dom.watchlistLoginInfo) {
            this.watchlistLoginInfo = new WatchlistLoginInfo(this.dom.watchlistLoginInfo);
        }
    }

    subsProps() {
        if (this.watchlistButton) {
            this.watchlistButton.updateProps({ id: this.props.jobHeader.job.id });
        }
        if (this.watchlistLoginInfo) {
            this.watchlistLoginInfo.updateProps({ id: this.props.jobHeader.job.id, jobDetailSpacing: true });
        }
    }

    closeWatchlistLoginInfo() {
        if (this.watchlistLoginInfo) {
            this.watchlistLoginInfo.updateProps({ visible: false, jobDetail: true }, true);
        }
    }

    isShareModalOpen() {
        return this.shareModal.isOpen();
    }

    closeShareModal() {
        this.shareModal.close();
    }

    openShareModal() {
        this.shareModal.open();
    }

    logApplicationClick(data) {
        postJson(
            `${API_ENDPOINT_LOG_APPLICATION_CLICK}`,
            { data },
        );
    }

    print() {
        window.print();
    }
}
